import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

// Per BLU-3036: the user's timezone is hard to determine inside background
// export workers, so we fall back to German time for the filename timestamp.
const DEFAULT_EXPORT_TIMEZONE = 'Europe/Berlin';

/**
 * Sanitizes a dynamic filename segment (e.g. address, scenario name) the same
 * way `getFilename` does: NFC-normalise, collapse whitespace/separators to a
 * single hyphen, drop characters outside [a-z0-9-] (German umlauts and ß are
 * kept), trim hyphens and lowercase. Keeping this identical to `getFilename`
 * means existing exports keep the address/name handling users already know.
 */
const sanitizeSegment = (value: string): string =>
  value
    .normalize('NFC') // NFC: to preserve German umlauts
    .replace(/[\s,:._-]+/g, '-') // Replace spaces & special chars with hyphen
    .replace(/[^a-zA-Z0-9-ßäöü]/g, '') // Remove remaining invalid chars (German umlauts and ß are allowed)
    .replace(/^-+|-+$/g, '') // Trim leading/trailing hyphens
    .toLowerCase();

/**
 * Builds an export filename following the standardised Predium convention:
 *
 *   YYYY-MM-DD_HH_MM_predium_<content>.<extension>
 *
 * `content` segments are joined with underscores after sanitisation. Fixed
 * keywords (e.g. "basic", "zia", "onepager") pass through unchanged, while
 * dynamic values (addresses, scenario names) are slugified. Empty segments are
 * dropped so a missing address never leaves a dangling separator.
 *
 * The timestamp uses German time (Europe/Berlin) by default — see BLU-3036.
 */
export function getExportFilename(
  content: string[],
  extension: string,
  options: { date?: Date; timeZone?: string } = {},
): string {
  const { date = new Date(), timeZone = DEFAULT_EXPORT_TIMEZONE } = options;

  const timestamp = dayjs(date).tz(timeZone).format('YYYY-MM-DD_HH_mm');
  const segments = content.map(sanitizeSegment).filter((segment) => segment.length > 0);
  const base = [timestamp, 'predium', ...segments].join('_');

  return extension ? `${base}.${extension}` : base;
}
